import { ALL_PERMISSIONS } from "../permissions.ts";
import { type Infer, v } from "../validation.ts";

export const password = () =>
  v
    .string({ min: 10, max: 200, trim: false })
    .refine((p) => p.trim().length >= 10, "Must be at least 10 characters");

/** Cloudflare Turnstile token (#42); required by the server only when Turnstile is on. */
const turnstileToken = () => v.string({ max: 4096 }).optional();

export const loginSchema = v.object({
  email: v.email(),
  password: v.string({ max: 200, trim: false }),
  turnstileToken: turnstileToken(),
});

const passkeyAssertion = () =>
  v
    .object({
      id: v.string({ max: 1024 }),
      response: v.object({
        clientDataJSON: v.string({ max: 8192 }),
        authenticatorData: v.string({ max: 8192 }),
        signature: v.string({ max: 2048 }),
      }),
    })
    .optional();

/** One second factor (#43): a code from an authenticator app, a recovery code or a passkey. */
export const secondFactorSchema = v.object({
  code: v.string({ max: 10 }).optional(),
  recoveryCode: v.string({ max: 32 }).optional(),
  passkey: passkeyAssertion(),
});

export const mfaLoginSchema = v.object({
  mfaToken: v.string({ max: 200 }),
  code: v.string({ max: 10 }).optional(),
  recoveryCode: v.string({ max: 32 }).optional(),
  passkey: passkeyAssertion(),
});

export const signupSchema = v.object({
  name: v.string({ min: 1, max: 120 }),
  email: v.email(),
  password: password(),
  acceptTerms: v.boolean().refine((b) => b, "Please accept the terms to continue"),
  turnstileToken: turnstileToken(),
});

export const resendVerificationSchema = v.object({ email: v.email(), turnstileToken: turnstileToken() });

/** First-run set-up for an individual landlord (#42). */
export const onboardingSchema = v.object({
  teamName: v.string({ min: 1, max: 120 }),
  ownerName: v.string({ min: 1, max: 200 }),
  accountingBasis: v.enum(["cash", "accruals"] as const),
});

export const changePasswordSchema = v.object({
  currentPassword: v.string({ max: 200, trim: false }),
  newPassword: password(),
});

export const forgotPasswordSchema = v.object({ email: v.email(), turnstileToken: turnstileToken() });

export const resetPasswordSchema = v.object({
  token: v.string({ max: 200 }),
  password: password(),
});

export const changeEmailSchema = v.object({
  newEmail: v.email(),
  password: v.string({ max: 200, trim: false }),
});

export const tokenSchema = v.object({ token: v.string({ max: 200 }) });

export const verifyEmailSchema = v.object({
  token: v.string({ max: 200 }),
  password: v.string({ min: 1, max: 200, trim: false }),
});

export const accountSchema = v.object({
  name: v.string({ min: 1, max: 120 }),
});

export const notificationPrefsSchema = v.object({
  emailEnabled: v.boolean(),
  inAppEnabled: v.boolean(),
  digest: v.enum(["off", "daily", "weekly"] as const),
  types: v.object({
    task: v.boolean(),
    compliance: v.boolean(),
    rent: v.boolean(),
    tenancy: v.boolean(),
    finance: v.boolean(),
    maintenance: v.boolean(),
    mtd: v.boolean(),
  }),
  taskReminderDays: v.int({ min: 0, max: 90 }).default(3),
});

export const acceptInviteSchema = v.object({
  token: v.string({ max: 200 }),
  name: v.string({ min: 1, max: 120 }).optional(),
  password: v.string({ max: 200, trim: false }),
});

export const switchTeamSchema = v.object({ teamId: v.uuid() });

export const teamSchema = v.object({
  name: v.string({ min: 1, max: 120 }),
});

export const teamSettingsSchema = v.object({
  name: v.string({ min: 1, max: 120 }),
  dateFormat: v.enum(["d MMM yyyy", "dd/MM/yyyy"] as const),
  timezone: v.string({ max: 64 }).default("Europe/London"),
  defaultRange: v.enum([
    "this_month",
    "previous_month",
    "this_year",
    "previous_year",
    "current_tax_year",
    "previous_tax_year",
    "last_12_months",
  ] as const),
  complianceWarningDays: v.int({ min: 1, max: 365 }).default(30),
  /** Every member must use two-step verification (#43). Only Owners can change it. */
  requireMfa: v.boolean(),
});

export const roleSchema = v.object({
  name: v.string({ min: 1, max: 60 }),
  description: v.string({ max: 300 }).default(""),
  permissions: v.array(v.enum(ALL_PERMISSIONS as unknown as readonly [string, ...string[]])),
});

export const inviteSchema = v.object({
  email: v.email(),
  roleId: v.uuid(),
});

export const memberRoleSchema = v.object({ roleId: v.uuid() });

export const DEFAULT_NOTIFICATION_PREFS: Infer<typeof notificationPrefsSchema> = {
  emailEnabled: true,
  inAppEnabled: true,
  digest: "daily",
  types: {
    task: true,
    compliance: true,
    rent: true,
    tenancy: true,
    finance: true,
    maintenance: true,
    mtd: true,
  },
  taskReminderDays: 3,
};
